import { IconButton, TextField } from "@material-ui/core";
import { isEmptyOrWhitespace, KeyValuePair } from "@shoothill/core";
import { useObserver } from "mobx-react-lite";
import React, { useEffect, useState } from "react";

import {
    ApprovalPanelContainer,
    ApprovalPanelAmendButton,
    ApprovalPanelApproveButton,
    ApprovalPanelFooter,
    ApprovalPanelRejectButton,
    ApprovalPanelSendButton,
    ApprovalPanelCallOutButton,
} from "Components/Approval/ApprovalPanel.styles";
import { DefaultButton } from "Components/Buttons/Buttons";
import { BaseSidebar } from "Components/Sidebar/BaseSidebar";
import { Uploader } from "Components/Uploader/Uploader";
import { ApprovalDocumentModel } from "./ApprovalDocumentModel";
import SystemUpdateTwoToneIcon from "@material-ui/icons/SystemUpdateTwoTone";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { MultiSelectView } from "Components/Select/MultiSelect";

interface IProps {
    sideOpen: boolean;
    upsertApprovalStatus: (
        isApproved: boolean,
        amendmentNote: string,
        requisitionStatusId: string,
        isCallout: boolean,
        selectedCalloutUsers: string[],
        isRequesterAmending: boolean,
    ) => void;
    bodyNewApprovalPanel: JSX.Element;
    bodyAmenderPanel: JSX.Element;
    bodyAmendedApprovalPanel: JSX.Element;
    bodyDefault: JSX.Element;
    canShowNewApprovalPanel: boolean;
    canShowAmenderPanel: boolean;
    canShowAmendedApprovalPanel: boolean;
    isFromApprovalSection: boolean;
    canUpsertStatus: boolean;
    primaryTitle: string;
    secondaryTitle: string | null;
    approvedStatusId: string;
    amendRequiredStatusId: string;
    rejectedStatusId: string;
    canViewFooter: boolean;
    isRequisition: boolean;
    isCentral: boolean;
    approvalCallOutUserOptions: KeyValuePair<any>[];
    isRequester: boolean;
    isAwaitingApproval: boolean;
    downloadFile?: (fileUrl: string, fileName: string) => Promise<void>;
    fileChange?: (event: any) => Promise<void>;
    approvalDocumentsToUpload?: ApprovalDocumentModel[];
    handleCancelAmend?: () => void;
    handleGeneratePreviewPDF?: () => Promise<void>;
    hideDefault?: boolean;
    setIsHide?: (isHide: boolean) => void;
}

export const ApprovalPanel: React.FC<IProps> = (props: IProps) => {
    const [isAmendingMode, setIsAmendingMode] = useState<boolean>(false);
    const [isCalloutMode, setIsCalloutMode] = useState<boolean>(false);
    const [amendmentNote, setAmendmentNote] = useState<string>("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedCallOutUsers, setSelectedCallOutUsers] = useState<string[]>([]); // Data to sent
    const [selectedCallOutUserNames, setSelectedCallOutUserNames] = useState<string>("None"); // Display only
    const [isRequesterAmending, setIsRequesterAmending] = useState<boolean>(false);

    useEffect(() => {
        setIsRequesterAmending(!props.canViewFooter && props.isRequester && props.isRequisition && props.isFromApprovalSection && props.isAwaitingApproval);
    }, [props.canViewFooter, props.isRequester, props.isRequisition, props.isFromApprovalSection, props.isAwaitingApproval]);

    const handleUpsertApprovalStatus = (isApproved: boolean, requisitionStatusId: string, isCallout: boolean, selectedCalloutUsers: string[], isRequesterAmending: boolean) => {
        props.upsertApprovalStatus(isApproved, amendmentNote, requisitionStatusId, isCallout, selectedCalloutUsers, isRequesterAmending);
    };

    const handleApprove = () => {
        const requisitionPOStatusId = props.approvedStatusId;
        handleUpsertApprovalStatus(true, requisitionPOStatusId ? requisitionPOStatusId : "", false, [], false);
    };

    const handleAmend = (isCallout: boolean = false) => {
        const requisitionPOStatusId = props.amendRequiredStatusId;
        setIsAmendingMode(false);
        setIsCalloutMode(false);
        const selectedUsers: string[] = selectedCallOutUsers;
        setSelectedCallOutUsers([]);
        setSelectedCallOutUserNames("None");

        handleUpsertApprovalStatus(false, requisitionPOStatusId ? requisitionPOStatusId : "", isCallout, selectedUsers, isRequesterAmending);
    };

    const handleReject = () => {
        const requisitionPOStatusId = props.rejectedStatusId;
        handleUpsertApprovalStatus(false, requisitionPOStatusId ? requisitionPOStatusId : "", false, [], false);
    };

    const cancelAmend = () => {
        setIsAmendingMode(false);
        setIsCalloutMode(false);
        setAmendmentNote("");
        setSelectedCallOutUsers([]);
        setSelectedCallOutUserNames("None");
        if (props.handleCancelAmend) {
            props.handleCancelAmend();
        }
    };

    const canExecuteSendRequiresAmendment = () => {
        if (isCalloutMode) {
            return selectedCallOutUsers.length > 0 && isCalloutMode && isAmendingMode && !isEmptyOrWhitespace(amendmentNote);
        }
        return isAmendingMode && !isEmptyOrWhitespace(amendmentNote);
    };

    const canExecuteCancelRequiresAmendment = () => {
        return isAmendingMode;
    };

    /**
     * Body component for a new approval. Shows when an approver needs to approve, require amend or reject an item.
     * @returns The component.
     */
    const bodyNewApprovalPanel = () => {
        return props.bodyNewApprovalPanel;
    };

    /**
     * Body component for amending an item. Shows when an amender needs to amend an item.
     * @returns The component.
     */
    const bodyAmenderPanel = () => {
        return props.bodyAmenderPanel;
    };

    /**
     * Body component for approving an amendment. Shows when an approver needs to approve an amendment.
     * @returns The component.
     */
    const bodyAmendedApprovalPanel = () => {
        return props.bodyAmendedApprovalPanel;
    };

    /**
     * The default Body component. Shows whenever the other body components don't show.
     * @returns The component.
     */
    const bodyDefault = () => {
        return props.bodyDefault;
    };

    /**
     * Determines which body component needs to be shown.
     * @returns The component to be shown.
     */
    const getBody = () => {
        if (props.canShowNewApprovalPanel) {
            return bodyNewApprovalPanel();
        } else if (props.canShowAmenderPanel) {
            return bodyAmenderPanel();
        } else if (props.canShowAmendedApprovalPanel) {
            return bodyAmendedApprovalPanel();
        } else {
            return bodyDefault();
        }
    };

    const handleGeneratePreviewPDF = async () => {
        if (!isGenerating) {
            setIsGenerating(true);

            try {
                if (props.handleGeneratePreviewPDF) {
                    await props.handleGeneratePreviewPDF();
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsGenerating(false);
            }
        }
    };

    const fileChange = async (e: any) => {
        if (props.fileChange) {
            props.fileChange(e);
        }
    };

    const handleSetSelectedCallOutUsers = (val: any) => {
        setSelectedCallOutUsers(val);
        const options: KeyValuePair[] = getApprovalCallOutUserOptions(false);

        const selectedNames: string[] = options.filter((o) => val.includes(o.value)).map((o) => o.key);

        if (selectedNames.length > 0) {
            setSelectedCallOutUserNames(selectedNames.join(", "));
        } else {
            setSelectedCallOutUserNames("None");
        }
    };

    const getApprovalCallOutUserOptions = (includeDeleted: boolean): KeyValuePair[] => {
        return props.approvalCallOutUserOptions === undefined ? [] : props.approvalCallOutUserOptions.slice();
    };

    const handleSetIsAmending = (val: boolean) => {
        setIsAmendingMode(true);
        setIsCalloutMode(false);
        setSelectedCallOutUsers([]);
        setSelectedCallOutUserNames("None");
    };

    const handleSetIsCallout = (val: boolean) => {
        setIsAmendingMode(true);
        setIsCalloutMode(true);
    };

    const footer = () => {
        // Show the amend button for the requester so they don't have to wait for an approver.
        if (isRequesterAmending) {
            return (
                <>
                    <ApprovalPanelFooter>
                        <ApprovalPanelAmendButton
                            displayName="Requires amend"
                            execute={() => handleSetIsAmending(true)}
                            fullWidth={true}
                            canExecute={!isAmendingMode && isRequesterAmending}
                        />

                        {isAmendingMode === true && (
                            <div className="amend-reason-container">
                                <TextField
                                    id="amend-reason"
                                    multiline
                                    rows={10}
                                    value={amendmentNote}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setAmendmentNote(event.target.value)}
                                    fullWidth
                                    className="textarea"
                                />
                                {props.fileChange && (
                                    <div>
                                        <Uploader handleSelectFile={(e: any) => fileChange(e)} isFormDisabled={!isAmendingMode} label="" />
                                        {props.approvalDocumentsToUpload && (
                                            <div className="uploadedFile">
                                                {props.approvalDocumentsToUpload.map((item: ApprovalDocumentModel, index: number) => {
                                                    return (
                                                        !item.isDeleted && (
                                                            <div className="uploadedFile-box">
                                                                <div className="uploadedFile-heading">
                                                                    <IconButton onClick={() => (props.downloadFile ? props.downloadFile(item.fileUrl, item.fileName) : () => {})}>
                                                                        <SystemUpdateTwoToneIcon />
                                                                    </IconButton>
                                                                    <h3>{item.fileName}</h3>
                                                                </div>
                                                                <IconButton onClick={() => item.handleDelete(true)}>
                                                                    <DeleteOutlinedIcon />
                                                                </IconButton>
                                                            </div>
                                                        )
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="amend-reason-button-container">
                                    <ApprovalPanelSendButton displayName="Send" execute={() => handleAmend(false)} canExecute={canExecuteSendRequiresAmendment()} />
                                    <DefaultButton displayName="Cancel" execute={() => cancelAmend()} canExecute={canExecuteCancelRequiresAmendment()} />
                                </div>
                            </div>
                        )}
                    </ApprovalPanelFooter>
                </>
            );
        } else if (props.canViewFooter) {
            // Show normal footer
            return (
                <>
                    <ApprovalPanelFooter>
                        {props.handleGeneratePreviewPDF && (
                            <p className={"pdf-link"} onClick={() => handleGeneratePreviewPDF()} style={{ cursor: isGenerating ? "not-allowed" : "pointer" }}>
                                Download PDF preview
                            </p>
                        )}
                        <ApprovalPanelApproveButton displayName="Approve" execute={() => handleApprove()} fullWidth={true} canExecute={!isAmendingMode && props.canUpsertStatus} />
                        <ApprovalPanelAmendButton
                            displayName="Requires amend"
                            execute={() => handleSetIsAmending(true)}
                            fullWidth={true}
                            canExecute={!isAmendingMode && props.canUpsertStatus}
                        />
                        {props.isRequisition && (
                            <ApprovalPanelCallOutButton
                                displayName="Call out"
                                execute={() => handleSetIsCallout(true)}
                                fullWidth={true}
                                canExecute={!isCalloutMode && !isAmendingMode && props.canUpsertStatus}
                            />
                        )}

                        {isAmendingMode === true && (
                            <div className="amend-reason-container">
                                <TextField
                                    id="amend-reason"
                                    multiline
                                    rows={10}
                                    value={amendmentNote}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setAmendmentNote(event.target.value)}
                                    fullWidth
                                    className="textarea"
                                />
                                {props.fileChange && (
                                    <div>
                                        <Uploader handleSelectFile={(e: any) => fileChange(e)} isFormDisabled={!isAmendingMode} label="" />
                                        {props.approvalDocumentsToUpload && (
                                            <div className="uploadedFile">
                                                {props.approvalDocumentsToUpload.map((item: ApprovalDocumentModel, index: number) => {
                                                    return (
                                                        !item.isDeleted && (
                                                            <div className="uploadedFile-box">
                                                                <div className="uploadedFile-heading">
                                                                    <IconButton onClick={() => (props.downloadFile ? props.downloadFile(item.fileUrl, item.fileName) : () => {})}>
                                                                        <SystemUpdateTwoToneIcon />
                                                                    </IconButton>
                                                                    <h3>{item.fileName}</h3>
                                                                </div>
                                                                <IconButton onClick={() => item.handleDelete(true)}>
                                                                    <DeleteOutlinedIcon />
                                                                </IconButton>
                                                            </div>
                                                        )
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {props.isRequisition && isCalloutMode && (
                                    <>
                                        {/**/}
                                        <p style={{ fontSize: "14px" }}>Selected users: {selectedCallOutUserNames}</p>
                                        <MultiSelectView
                                            style={{ width: "100%", marginBottom: "20px" }}
                                            display="Users:"
                                            fieldName="callOutUsers"
                                            values={selectedCallOutUsers}
                                            getOptions={getApprovalCallOutUserOptions}
                                            handleChange={(values: number[] | null) => handleSetSelectedCallOutUsers(values)}
                                        />
                                    </>
                                )}

                                <div className="amend-reason-button-container">
                                    <ApprovalPanelSendButton displayName="Send" execute={() => handleAmend(isCalloutMode)} canExecute={canExecuteSendRequiresAmendment()} />
                                    <DefaultButton displayName="Cancel" execute={() => cancelAmend()} canExecute={canExecuteCancelRequiresAmendment()} />
                                </div>
                            </div>
                        )}
                        <ApprovalPanelRejectButton displayName="Reject" execute={() => handleReject()} fullWidth={true} canExecute={!isAmendingMode && props.canUpsertStatus} />
                    </ApprovalPanelFooter>
                </>
            );
        }

        // Otherwise show nothing.
        return <></>;
    };

    return useObserver(() => {
        return (
            <ApprovalPanelContainer>
                <BaseSidebar
                    open={props.sideOpen}
                    primaryTitle={props.primaryTitle}
                    secondaryTitle={props.secondaryTitle}
                    onClose={() => {}}
                    bodyContent={getBody()}
                    footerContent={props.canViewFooter || props.isRequester ? footer() : undefined}
                    hideDefault={props.hideDefault}
                    setIsHide={props.setIsHide}
                />
            </ApprovalPanelContainer>
        );
    });
};
